import { formatEpoch, ownValue, tidy } from "../shared/safe";

// Pure parsing and validation of the Client Detail page's API replies. Everything the page reads from the server is
// untrusted: strings may hold markup, stored JSON columns may be malformed, URLs may be `javascript:`, platform keys may
// be "toString". Nothing in here touches the DOM; the views render only what these functions return.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);

/** http(s) only, normalised; null for anything else (javascript:, data:, relative, garbage). */
export function httpUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const u = new URL(value.trim());
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

export interface LinkView {
  text: string;
  /** Null when the value is not a safe http(s) URL: it is then shown as plain text, not as a link. */
  href: string | null;
}
const link = (value: unknown): LinkView | null => {
  const s = str(value);
  return s === null ? null : { text: tidy(s, 300), href: httpUrl(s) };
};

// ---- client -----------------------------------------------------------------------------------------------

export const PAGE_TYPE_ORDER = ["landing", "blog", "service", "location", "about", "contact"] as const;
export type PageType = (typeof PAGE_TYPE_ORDER)[number];

export interface ClientView {
  id: string;
  name: string;
  slug: string;
  category: string | null;
  addressLine: string | null;
  phone: string | null;
  owner: string | null;
  moneySite: LinkView | null;
  services: string[];
  serviceArea: string[];
  attributes: { label: string; value: string }[];
  claimBlock: string | null;
  templateSelection: Partial<Record<PageType, string>>;
}

/** Parses a stored JSON column. Missing is fine; malformed or the wrong shape is reported, never thrown. */
function storedJson(raw: unknown, label: string, warnings: string[]): unknown {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw !== "string") {
    warnings.push(`${label} is stored in an unexpected format and was ignored.`);
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    warnings.push(`${label} is stored as malformed JSON and could not be read.`);
    return null;
  }
}

export function parseClientResponse(body: unknown): { client: ClientView; warnings: string[] } | null {
  if (!isObject(body) || !isObject(body.client)) return null;
  const c = body.client;
  const id = str(c.id);
  if (id === null) return null;
  const warnings: string[] = [];

  const servicesRaw = storedJson(c.services_json, "Services", warnings);
  const services: string[] = [];
  if (servicesRaw !== null) {
    if (!Array.isArray(servicesRaw)) warnings.push("Services are stored in an unexpected shape and were ignored.");
    else {
      for (const s of servicesRaw) {
        const name = isObject(s) ? str(s.name) : str(s);
        if (name !== null) services.push(tidy(name, 120));
        else warnings.push("A service entry without a name was skipped.");
      }
    }
  }

  const areaRaw = storedJson(c.service_area_json, "Service area", warnings);
  const serviceArea: string[] = [];
  if (areaRaw !== null) {
    if (!Array.isArray(areaRaw)) warnings.push("Service area is stored in an unexpected shape and was ignored.");
    else for (const a of areaRaw) if (str(a) !== null) serviceArea.push(tidy(a, 120));
  }

  const attrRaw = storedJson(c.attributes_json, "Attributes", warnings);
  const attributes: { label: string; value: string }[] = [];
  if (attrRaw !== null) {
    if (!Array.isArray(attrRaw)) warnings.push("Attributes are stored in an unexpected shape and were ignored.");
    else {
      for (const a of attrRaw) {
        if (isObject(a) && str(a.label) !== null && (typeof a.value === "string" || typeof a.value === "number")) attributes.push({ label: tidy(a.label, 120), value: tidy(a.value, 300) });
        else warnings.push("An attribute entry was skipped because it was not a label and a value.");
      }
    }
  }

  const selRaw = storedJson(c.template_selection_json, "Template selection", warnings);
  const templateSelection: Partial<Record<PageType, string>> = {};
  if (selRaw !== null) {
    if (!isObject(selRaw)) warnings.push("Template selection is stored in an unexpected shape; every picker is shown as unset.");
    else for (const t of PAGE_TYPE_ORDER) if (Object.prototype.hasOwnProperty.call(selRaw, t) && typeof selRaw[t] === "string") templateSelection[t] = selRaw[t] as string;
  }

  const place = [str(c.city), [str(c.state), str(c.zip)].filter(Boolean).join(" ") || null].filter(Boolean).join(", ");
  const addressLine = [str(c.address), place || null].filter(Boolean).join(", ") || null;

  return {
    client: {
      id,
      name: str(c.business_name) ? tidy(c.business_name, 300) : "(unnamed client)",
      slug: tidy(c.slug, 120),
      category: str(c.primary_category) ? tidy(c.primary_category, 200) : null,
      addressLine: addressLine ? tidy(addressLine, 400) : null,
      phone: str(c.phone) ? tidy(c.phone, 60) : null,
      owner: str(c.owner_name) ? tidy(c.owner_name, 200) : null,
      moneySite: link(c.money_site_url),
      services,
      serviceArea,
      attributes,
      claimBlock: str(c.claim_block),
      templateSelection,
    },
    warnings,
  };
}

// ---- sites, connections, templates, satellites ------------------------------------------------------------

export interface SiteView {
  platform: string;
  url: LinkView | null;
  status: string;
  lastDeploy: string | null;
}

/** Only platform, url, status and last_deploy_at are read: the route returns SELECT *, and other columns must never reach the page. */
export function parseSites(body: unknown): SiteView[] | null {
  if (!isObject(body) || !Array.isArray(body.sites)) return null;
  return body.sites.filter(isObject).map((s) => ({
    platform: str(s.platform) ?? "unknown",
    url: link(s.url),
    status: str(s.status) ?? "unknown",
    lastDeploy: formatEpoch(s.last_deploy_at)?.text ?? null,
  }));
}

/** A connection that can actually be used to publish: the backend resolves destinations with exactly these statuses. */
export const USABLE_CONNECTION_STATUSES = ["connected", "expiring"] as const;

export interface ConnectionView {
  platform: string;
  scope: string;
  identity: string | null;
  status: string;
  usable: boolean;
}
export interface PlatformView {
  platform: string;
  category: string;
}

export function parseConnections(body: unknown): { connections: ConnectionView[]; platforms: PlatformView[] } | null {
  if (!isObject(body) || !Array.isArray(body.connections) || !Array.isArray(body.availablePlatforms)) return null;
  const connections = body.connections.filter(isObject).flatMap((c) => {
    const platform = str(c.platform);
    if (platform === null) return [];
    const status = str(c.status) ?? "unknown";
    return [{ platform, scope: str(c.scope) ?? "", identity: str(c.identity) ? tidy(c.identity, 120) : null, status, usable: (USABLE_CONNECTION_STATUSES as readonly string[]).includes(status) }];
  });
  const platforms = body.availablePlatforms.filter(isObject).flatMap((p) => (str(p.platform) !== null ? [{ platform: p.platform as string, category: str(p.category) ?? "unknown" }] : []));
  return { connections, platforms };
}

export interface TemplateView {
  id: string;
  name: string;
  pageType: string;
}
export function parseTemplates(body: unknown): TemplateView[] | null {
  if (!isObject(body) || !Array.isArray(body.templates)) return null;
  return body.templates.filter(isObject).flatMap((t) => (str(t.id) !== null && str(t.page_type) !== null ? [{ id: t.id as string, name: str(t.name) ? tidy(t.name, 120) : "(unnamed template)", pageType: t.page_type as string }] : []));
}

export interface SatelliteView {
  id: string;
  name: string;
  serviceKey: string;
  slug: string;
  status: string;
  sites: SiteView[];
}
export function parseSatellites(body: unknown): SatelliteView[] | null {
  if (!isObject(body) || !Array.isArray(body.satellites)) return null;
  return body.satellites.filter(isObject).flatMap((s) => {
    const id = str(s.id);
    if (id === null) return [];
    return [
      {
        id,
        name: str(s.name) ? tidy(s.name, 160) : "(unnamed satellite)",
        serviceKey: str(s.service_key) ?? "",
        slug: tidy(s.slug, 120),
        status: str(s.status) ?? "unknown",
        sites: parseSites({ sites: s.sites }) ?? [],
      },
    ];
  });
}

// ---- content ---------------------------------------------------------------------------------------------------

export interface MasterView {
  id: string;
  title: string;
  status: string;
  model: string | null;
}
export function parseMasters(body: unknown): MasterView[] | null {
  if (!isObject(body) || !Array.isArray(body.masters)) return null;
  return body.masters.filter(isObject).flatMap((m) => {
    const id = str(m.id);
    return id === null ? [] : [{ id, title: str(m.title) ? tidy(m.title, 300) : "(untitled)", status: str(m.status) ?? "unknown", model: str(m.model_used) ? tidy(m.model_used, 80) : null }];
  });
}

export const DELIVERY_STATES = ["not_submitted", "queued", "partial", "delivered", "failed"] as const;
export type DeliveryStateName = (typeof DELIVERY_STATES)[number] | "unknown";

/** A destination's CURRENT state as the server derives it from recorded outcomes. `null` = an older server that does not say. */
export const DESTINATION_STATES = ["delivered", "queued", "failed", "needs_reconciliation"] as const;
export type DestinationStateName = (typeof DESTINATION_STATES)[number] | "unknown";

/** The immutable receipt the PROVIDER returned for a destination. Never a predicted or derived address. */
export interface ReceiptView {
  providerUrl: LinkView | null;
  releaseHash: string | null;
  accountProvenance: string | null;
  recordedAt: string | null;
  /** The account (connection row) and credential revision the receipt was recorded under. Null on servers before migration 0008 and on historical receipts. */
  connectionId: string | null;
  accountRevision: number | null;
  /** The job that produced the receipt. Null on servers before round 5. */
  jobId: string | null;
}

export interface DeliveryDestinationView {
  jobId: string;
  platform: string;
  connectionId: string | null;
  /** The credential revision of `connectionId` this attempt pinned. Null on pre-0008 jobs. */
  accountRevision: number | null;
  submissionId: string | null;
  jobStatus: string;
  sideEffect: string | null;
  attempts: number | null;
  lastError: string | null;
  /** Current state of this destination; null when the server predates it (only then is jobStatus interpreted). */
  state: DestinationStateName | null;
  destinationState: string | null;
  /** A newer attempt is queued or running even though an earlier delivery stands. */
  inFlight: boolean;
  /**
   * Queue dispatch of THIS attempt's job, from the server's durable outbox: "pending" = accepted and recorded but not (yet) on the
   * queue, waiting for the dispatcher; "sent" = handed to the queue; null = not stated (an older server, or a legacy job).
   * Dispatch is not delivery: only `state` and a receipt say anything about delivery.
   */
  dispatch: "pending" | "sent" | null;
  /** The receipt that belongs to THIS attempt's account. Never one from another account or credential revision. */
  receipt: ReceiptView | null;
  /** Delivery evidence for this destination that is NOT this account's: history only, never counted as delivered. */
  priorReceipts: ReceiptView[];
  /** This page moved the server's receipt out of `receipt` because it provably belongs to another account (defence in depth; the server already attributes). */
  attributionConflict: boolean;
}
export interface HistoryEntryView {
  jobId: string;
  platform: string;
  connectionId: string | null;
  accountRevision: number | null;
  submissionId: string | null;
  jobStatus: string;
  sideEffect: string | null;
  lastError: string | null;
  receipt: ReceiptView | null;
}
export interface SubmissionView {
  id: string;
  createdAt: string | null;
  acceptedCount: number | null;
  skippedCount: number;
}
export interface DeliveryView {
  state: DeliveryStateName;
  destinations: DeliveryDestinationView[];
  confirmed: number;
  needsReconciliation: number;
  /** Every attempt of every submission, kept apart: receipts are never merged across attempts or accounts. */
  history: HistoryEntryView[];
  submissions: SubmissionView[];
}

const revisionOf = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

function readReceipt(v: unknown): ReceiptView | null {
  if (!isObject(v)) return null;
  const providerUrl = link(v.providerUrl);
  if (providerUrl === null) return null;
  return {
    providerUrl,
    releaseHash: str(v.releaseHash) ? tidy(v.releaseHash, 80) : null,
    accountProvenance: str(v.accountProvenance) ? tidy(v.accountProvenance, 40) : null,
    recordedAt: formatEpoch(v.recordedAt)?.text ?? null,
    connectionId: str(v.connectionId) ? tidy(v.connectionId, 60) : null,
    accountRevision: revisionOf(v.accountRevision),
    jobId: str(v.jobId) ? tidy(v.jobId, 60) : null,
  };
}

/**
 * Whether a receipt may be shown as THIS destination's current delivery. The server already attributes receipts to the
 * proven account (connection id + credential revision, R5-A4); this is the page's own check so that a receipt which
 * provably belongs to another account, or whose account was never proven and which another job produced, can never be
 * painted as the current account's delivery even if a server (or a bug) puts it in `receipt`.
 *  - both sides name an account and revision and they differ: another account;
 *  - the receipt names its job, that job is not this attempt, and either side lacks a proven revision: unproven, so not this account's.
 * A server that sends neither connection nor job on receipts (before round 5) cannot be checked and is taken as sent.
 */
export function receiptIsThisAccounts(r: ReceiptView, d: { jobId: string; connectionId: string | null; accountRevision: number | null }): boolean {
  if (r.connectionId !== null && r.accountRevision !== null && d.connectionId !== null && d.accountRevision !== null) {
    return r.connectionId === d.connectionId && r.accountRevision === d.accountRevision;
  }
  if (r.jobId !== null && r.jobId !== d.jobId) return false;
  return true;
}

/** `delivery` from GET /v1/content/masters/:id. The editorial status is read separately and never inferred from it. */
export function parseMasterDetail(body: unknown): { status: string | null; delivery: DeliveryView | null } | null {
  if (!isObject(body)) return null;
  const master = isObject(body.master) ? body.master : null;
  const d = isObject(body.delivery) ? body.delivery : null;
  let delivery: DeliveryView | null = null;
  if (d) {
    const state = typeof d.state === "string" && (DELIVERY_STATES as readonly string[]).includes(d.state) ? (d.state as DeliveryStateName) : "unknown";
    const destinations = (Array.isArray(d.destinations) ? d.destinations : []).filter(isObject).map((x): DeliveryDestinationView => {
      const dstate = typeof x.state === "string" ? ((DESTINATION_STATES as readonly string[]).includes(x.state) ? (x.state as DestinationStateName) : "unknown") : null;
      return {
        jobId: str(x.jobId) ?? "",
        platform: str(x.platform) ?? "unknown",
        connectionId: str(x.connectionId),
        accountRevision: revisionOf(x.accountRevision),
        submissionId: str(x.submissionId),
        jobStatus: str(x.jobStatus) ?? "unknown",
        sideEffect: str(x.sideEffect),
        attempts: typeof x.attempts === "number" && Number.isFinite(x.attempts) ? x.attempts : null,
        lastError: str(x.lastError),
        state: dstate,
        destinationState: str(x.destinationState),
        inFlight: x.inFlight === true,
        dispatch: x.dispatch === "pending" || x.dispatch === "sent" ? x.dispatch : null,
        receipt: readReceipt(x.receipt),
        priorReceipts: (Array.isArray(x.priorReceipts) ? x.priorReceipts : []).map(readReceipt).filter((r): r is ReceiptView => r !== null),
        attributionConflict: false,
      };
    });
    const history = (Array.isArray(d.history) ? d.history : []).filter(isObject).map((x): HistoryEntryView => ({
      jobId: str(x.jobId) ?? "",
      platform: str(x.platform) ?? "unknown",
      connectionId: str(x.connectionId),
      accountRevision: revisionOf(x.accountRevision),
      submissionId: str(x.submissionId),
      jobStatus: str(x.jobStatus) ?? "unknown",
      sideEffect: str(x.sideEffect),
      lastError: str(x.lastError),
      receipt: readReceipt(x.receipt),
    }));
    const submissions = (Array.isArray(d.submissions) ? d.submissions : []).filter(isObject).flatMap((x): SubmissionView[] =>
      str(x.id) === null ? [] : [{ id: x.id as string, createdAt: formatEpoch(x.createdAt)?.text ?? null, acceptedCount: typeof x.acceptedCount === "number" && Number.isFinite(x.acceptedCount) ? x.acceptedCount : null, skippedCount: Array.isArray(x.skipped) ? x.skipped.length : 0 }],
    );
    const count = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
    delivery = { state, destinations, confirmed: count(d.confirmed), needsReconciliation: count(d.needsReconciliation), history, submissions };
    attributeReceipts(delivery);
  }
  return { status: master ? str(master.status) : null, delivery };
}

/**
 * Fail closed on receipt attribution. A receipt in `receipt` that is not this attempt's account's (see receiptIsThisAccounts) is
 * moved to `priorReceipts`. If that receipt was the only thing holding the destination "delivered" (the job itself did not
 * finish), the destination is no longer shown as delivered, and the summary counts and state are recomputed so the page never
 * claims a delivery for the current account that only an earlier account made.
 */
function attributeReceipts(d: DeliveryView): void {
  let downgraded = false;
  for (const x of d.destinations) {
    if (x.receipt === null || receiptIsThisAccounts(x.receipt, x)) continue;
    x.priorReceipts = [x.receipt, ...x.priorReceipts];
    x.receipt = null;
    x.attributionConflict = true;
    if (x.state === "delivered" && x.jobStatus !== "done") {
      x.state = "unknown";
      x.inFlight = false;
      downgraded = true;
    }
  }
  if (!downgraded) return;
  const delivered = d.destinations.filter((x) => effectiveDestinationState(x) === "delivered").length;
  d.confirmed = delivered;
  if (d.state === "delivered" || d.state === "partial") d.state = delivered === 0 ? (d.destinations.some((x) => ["queued", "running", "failed"].includes(x.jobStatus)) ? "queued" : "failed") : delivered === d.destinations.length ? "delivered" : "partial";
}

/**
 * What to show for one destination. The server's CURRENT `state` decides. Only a server that sends no `state` at all
 * (before round 4) is interpreted from the job row, and even then a finished job is never upgraded to "delivered"
 * when the row also records a possible or confirmed unrecorded effect.
 */
export function effectiveDestinationState(d: DeliveryDestinationView): DestinationStateName {
  if (d.state !== null) return d.state;
  if (d.jobStatus === "dead") return d.sideEffect === "possible" || d.sideEffect === "confirmed" ? "needs_reconciliation" : "failed";
  if (d.jobStatus === "done") return d.sideEffect === "possible" ? "needs_reconciliation" : "delivered";
  if (d.jobStatus === "queued" || d.jobStatus === "running" || d.jobStatus === "failed") return "queued";
  return "unknown";
}

/** Editorial states (the article's own status), labelled in plain words. Unknown values are shown as given. */
const EDITORIAL: Record<string, { label: string; tone: "ok" | "warn" | "bad" | "neutral" }> = {
  draft: { label: "Draft", tone: "neutral" },
  waiting_review: { label: "Waiting for review", tone: "warn" },
  approved: { label: "Approved", tone: "neutral" },
  published: { label: "Published", tone: "ok" },
  failed: { label: "Failed", tone: "bad" },
};
export const editorialStatus = (status: string): { label: string; tone: "ok" | "warn" | "bad" | "neutral" } => ownValue(EDITORIAL, status) ?? { label: tidy(status, 30), tone: "neutral" };

export function siteTone(status: string): "ok" | "bad" | "neutral" {
  return status === "live" ? "ok" : status === "failed" ? "bad" : "neutral";
}
